import { useState, useEffect } from 'react';
import { Building2, Plus, Search, X } from 'lucide-react';
import Layout from '../components/shared/Layout';
import FacilityCard from '../components/facilities/FacilityCard';
import { apiGet, apiPost, apiPut, apiDelete } from '../config/api';

interface Facility {
    id: string;
    name: string;
    address: string;
    totalArea: number;
    sectorType: 'TERTIARY' | 'INDUSTRIAL';
    riskLevel: 'STANDARD' | 'ELEVATED' | 'HIGH';
    createdAt: number;
    updatedAt: number;
}

export default function FacilitiesPage() {
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterRisk, setFilterRisk] = useState<string>('ALL');
    const [filterSector, setFilterSector] = useState<string>('ALL');
    const [showModal, setShowModal] = useState(false);
    const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        totalArea: '',
        sectorType: 'TERTIARY' as 'TERTIARY' | 'INDUSTRIAL',
        riskLevel: 'STANDARD' as 'STANDARD' | 'ELEVATED' | 'HIGH',
    });

    useEffect(() => {
        fetchFacilities();
    }, []);

    const fetchFacilities = async () => {
        try {
            setLoading(true);
            const response = await apiGet('/api/facilities');
            const data = await response.json() as { success: boolean; facilities: Facility[] };
            if (data.success) {
                setFacilities(data.facilities);
            }
        } catch (error) {
            console.error('Failed to fetch facilities:', error);
            // Fallback to empty array on error
            setFacilities([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const payload = {
            ...formData,
            totalArea: parseFloat(formData.totalArea),
        };

        try {
            const url = editingFacility
                ? `https://fire-safety-api.tedguy280.workers.dev/api/facilities/${editingFacility.id}`
                : 'https://fire-safety-api.tedguy280.workers.dev/api/facilities';

            const response = await fetch(url, {
                method: editingFacility ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json() as { success: boolean };
            if (data.success) {
                await fetchFacilities();
                resetForm();
            }
        } catch (error) {
            console.error('Failed to save facility:', error);
        }
    };

    const handleDelete = async (facilityId: string) => {
        if (!window.confirm('Are you sure you want to delete this facility? This cannot be undone.')) {
            return;
        }

        try {
            const response = await fetch(`https://fire-safety-api.tedguy280.workers.dev/api/facilities/${facilityId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });

            const data = await response.json() as { success: boolean };
            if (data.success) {
                await fetchFacilities();
            }
        } catch (error) {
            console.error('Failed to delete facility:', error);
        }
    };

    const handleEdit = (facility: Facility) => {
        setEditingFacility(facility);
        setFormData({
            name: facility.name,
            address: facility.address,
            totalArea: facility.totalArea.toString(),
            sectorType: facility.sectorType,
            riskLevel: facility.riskLevel,
        });
        setShowModal(true);
    };

    const handleAddNew = () => {
        resetForm();
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            address: '',
            totalArea: '',
            sectorType: 'TERTIARY',
            riskLevel: 'STANDARD',
        });
        setShowModal(false);
        setEditingFacility(null);
    };

    const filteredFacilities = facilities.filter((facility) => {
        const matchesSearch =
            facility.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            facility.address.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRisk = filterRisk === 'ALL' || facility.riskLevel === filterRisk;
        const matchesSector = filterSector === 'ALL' || facility.sectorType === filterSector;
        return matchesSearch && matchesRisk && matchesSector;
    });

    return (
        <Layout>
            <div className="min-h-screen p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8 animate-fadeIn">
                    <div>
                        <h1 className="text-display-1 font-bold text-charcoal-950 mb-2 flex items-center gap-3">
                            <Building2 className="w-10 h-10 text-ember-500" />
                            Facilities
                        </h1>
                        <p className="text-steel-600">
                            Manage your fire safety facilities and compliance zones
                        </p>
                    </div>
                    <button
                        onClick={handleAddNew}
                        className="btn btn-primary flex items-center gap-2 group"
                    >
                        <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                        Add Facility
                    </button>
                </div>

                {/* Filters */}
                <div className="card-glass p-6 mb-6 animate-slideUp">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Search */}
                        <div className="md:col-span-1">
                            <label className="label">Search</label>
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="input pl-12"
                                    placeholder="Search by name or address..."
                                />
                            </div>
                        </div>

                        {/* Risk Level Filter */}
                        <div>
                            <label className="label">Risk Level</label>
                            <select
                                value={filterRisk}
                                onChange={(e) => setFilterRisk(e.target.value)}
                                className="input"
                            >
                                <option value="ALL">All Levels</option>
                                <option value="STANDARD">Standard</option>
                                <option value="ELEVATED">Elevated</option>
                                <option value="HIGH">High</option>
                            </select>
                        </div>

                        {/* Sector Type Filter */}
                        <div>
                            <label className="label">Sector Type</label>
                            <select
                                value={filterSector}
                                onChange={(e) => setFilterSector(e.target.value)}
                                className="input"
                            >
                                <option value="ALL">All Sectors</option>
                                <option value="TERTIARY">Tertiary</option>
                                <option value="INDUSTRIAL">Industrial</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="card-glass p-6 animate-pulse">
                                <div className="h-6 bg-steel-700 rounded mb-4"></div>
                                <div className="h-4 bg-steel-700 rounded mb-2"></div>
                                <div className="h-4 bg-steel-700 rounded w-2/3"></div>
                            </div>
                        ))}
                    </div>
                ) : filteredFacilities.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredFacilities.map((facility, index) => (
                            <div key={facility.id} style={{ animationDelay: `${index * 50}ms` }}>
                                <FacilityCard
                                    facility={facility}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="card-glass p-12 text-center animate-scaleIn">
                        <Building2 className="w-16 h-16 text-steel-400 mx-auto mb-4" />
                        <h3 className="font-display font-semibold text-xl text-charcoal-900 mb-2">
                            No facilities found
                        </h3>
                        <p className="text-steel-600 mb-6">
                            {searchQuery || filterRisk !== 'ALL' || filterSector !== 'ALL'
                                ? 'Try adjusting your filters'
                                : 'Get started by adding your first facility'}
                        </p>
                        {!searchQuery && filterRisk === 'ALL' && filterSector === 'ALL' && (
                            <button onClick={handleAddNew} className="btn btn-primary">
                                <Plus className="w-5 h-5 mr-2" />
                                Add Your First Facility
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="card-glass max-w-2xl w-full p-8 animate-scaleIn">
                        {/* Modal Header */}
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-display font-bold text-white">
                                {editingFacility ? 'Edit Facility' : 'Add New Facility'}
                            </h2>
                            <button
                                onClick={resetForm}
                                className="p-2 rounded-lg hover:bg-steel-700/50 transition-colors"
                            >
                                <X className="w-6 h-6 text-steel-400" />
                            </button>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="label">Facility Name</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="input"
                                        placeholder="e.g., Main Office Building"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="label">Total Area (m²)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.totalArea}
                                        onChange={(e) => setFormData({ ...formData, totalArea: e.target.value })}
                                        className="input"
                                        placeholder="2500"
                                        required
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="label">Address</label>
                                    <input
                                        type="text"
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        className="input"
                                        placeholder="123 Business Park, Paris 75001"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="label">Sector Type</label>
                                    <select
                                        value={formData.sectorType}
                                        onChange={(e) => setFormData({ ...formData, sectorType: e.target.value as 'TERTIARY' | 'INDUSTRIAL' })}
                                        className="input"
                                    >
                                        <option value="TERTIARY">Tertiary</option>
                                        <option value="INDUSTRIAL">Industrial</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="label">Risk Level</label>
                                    <select
                                        value={formData.riskLevel}
                                        onChange={(e) => setFormData({ ...formData, riskLevel: e.target.value as 'STANDARD' | 'ELEVATED' | 'HIGH' })}
                                        className="input"
                                    >
                                        <option value="STANDARD">Standard</option>
                                        <option value="ELEVATED">Elevated</option>
                                        <option value="HIGH">High</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end pt-4 border-t border-steel-700/50">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    {editingFacility ? 'Update Facility' : 'Create Facility'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}
